// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useListSelection, useMergedConfig } from './hooks'
import { ViewListStyled, viewListClasses as classes } from './styled'
import { LIST_HEADER_HEIGHT, CHECKBOX_COL_WIDTH, mapSxTableCell, resolveFlexColumnWidths, resolveListSpacerHeight } from './helpers'
// types
import type { TableContainerProps } from '@mui/material'
import type { ComponentType, FC, ReactNode } from 'react'
import type { LoadingModeRule, TPagination } from '../types'
import type { IListCellParams, IViewListConfig, IViewListProps, TListColumn } from './types'

export * from './types'

//#region Helpers
function renderCellValue<T>(column: TListColumn<T>, row: T, index: number): ReactNode {
  const currentValue = (row as any)[column.field]
  const value = column.valueGetter ? column.valueGetter(currentValue, row, index) : currentValue
  const params: IListCellParams<T> = { value, row, index, field: column.field }
  if (column.renderCell) return column.renderCell(params)
  const valueLabel = String(value ?? '')
  return (
    <Typography variant='body2' noWrap title={valueLabel}>
      {valueLabel}
    </Typography>
  )
}

function renderColGroup(resolvedWidths: Array<number | string | undefined>, selectable: boolean) {
  return (
    <colgroup>
      {selectable && <col style={{ width: CHECKBOX_COL_WIDTH }} />}
      {resolvedWidths.map((w, i) => (
        <col key={i} style={w !== undefined ? { width: w as any } : undefined} />
      ))}
    </colgroup>
  )
}

//#endregion
//#region Normal List
function createViewListNormal<T>(config: IViewListConfig<T>) {
  const ViewListNormal: FC<IViewListProps<T>> = (props) => {
    const mergedConfig = useMergedConfig(config, props)
    const tableMinWidthRaw = mergedConfig.columns.reduce((sum, col) => {
      if (col.width !== undefined) return sum + (typeof col.width === 'number' ? col.width : 0)
      return sum + (col.minWidth ?? 0)
    }, 0)
    const tableMinWidth = (mergedConfig.selectable ? CHECKBOX_COL_WIDTH : 0) + tableMinWidthRaw

    const { normalOptions } = mergedConfig
    const isAutoHeight = normalOptions.autoHeight == true

    const onSelectionChange = props.onSelectionChange
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const [containerWidth, setContainerWidth] = useState(0)

    useLayoutEffect(() => {
      if (!scrollContainerRef.current) return
      const el = scrollContainerRef.current
      const update = () =>
        setContainerWidth((prev) => {
          const next = el.clientWidth
          return prev === next ? prev : next
        })
      update()
      const observer = new ResizeObserver(update)
      observer.observe(el)
      return () => observer.disconnect()
    }, [])

    const resolvedWidths = useMemo(
      () => resolveFlexColumnWidths(mergedConfig.columns, mergedConfig.selectable, containerWidth),
      [mergedConfig.columns, mergedConfig.selectable, containerWidth]
    )

    // Reset scroll to top when token changes
    useEffect(() => {
      if (isAutoHeight || props.scrollResetToken === undefined) return
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0
    }, [props.scrollResetToken])

    const handleScroll: TableContainerProps['onScroll'] = (e) => {
      if (isAutoHeight) return
      const t = e.currentTarget
      if (t.scrollHeight - t.scrollTop - t.clientHeight <= mergedConfig.nearEndThreshold) {
        props.onNearEnd?.()
      }
    }

    // Use selection hook to manage checkbox state and handlers
    const selection = useListSelection(props.value, config.getterId, mergedConfig.selectable, props.selectedIds, onSelectionChange)
    const { selectedIds, isAllSelected, isSomeSelected, handleToggleAll, handleToggleRow } = selection

    const rootClasses = useMemo(() => {
      const l = [classes.root, classes.normal]
      if (isAutoHeight) l.push(classes.autoHeight)
      return l.join(' ')
    }, [isAutoHeight])

    return (
      <ViewListStyled className={rootClasses} style={mergedConfig.rootStyle}>
        <TableContainer ref={scrollContainerRef} className={classes.scrollContainer} onScroll={handleScroll}>
          <Table size='small' className={classes.table} sx={{ minWidth: tableMinWidth }}>
            {renderColGroup(resolvedWidths, mergedConfig.selectable)}
            <TableHead className={classes.tableHeader}>
              <TableRow>
                {mergedConfig.selectable && (
                  <TableCell padding='checkbox' className={classes.tableHeaderCell}>
                    <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleToggleAll} />
                  </TableCell>
                )}
                {mergedConfig.columns.map((col, colIndex) => (
                  <TableCell
                    key={String(col.field)}
                    className={classes.tableHeaderCell}
                    align={col.align}
                    sx={mapSxTableCell(col, resolvedWidths[colIndex])}
                  >
                    {col.label ?? String(col.field)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.value.map((row, index) => {
                const rowKey = config.getterId(row, index) ?? index
                const isSelected = selectedIds.includes(rowKey)
                const { rowHeight, cellPadding } = mergedConfig
                return (
                  <TableRow key={rowKey} hover sx={{ height: rowHeight }} selected={isSelected}>
                    {mergedConfig.selectable && (
                      <TableCell padding='checkbox' className={classes.tableBodyCell} sx={{ height: rowHeight, padding: cellPadding }}>
                        <Checkbox checked={isSelected} onChange={() => handleToggleRow(rowKey)} />
                      </TableCell>
                    )}
                    {mergedConfig.columns.map((col, colIndex) => (
                      <TableCell
                        key={`${String(rowKey)}-${String(col.field)}`}
                        className={classes.tableBodyCell}
                        align={col.align}
                        sx={mapSxTableCell(col, resolvedWidths[colIndex], { height: rowHeight, padding: cellPadding })}
                      >
                        <Box className={classes.tableCellContent}>{renderCellValue(col, row, index)}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </ViewListStyled>
    )
  }
  return ViewListNormal
}
//#endregion
//#region Virtualized List
function createViewListVirtualized<T>(config: IViewListConfig<T>) {
  const ViewListVirtualized: FC<IViewListProps<T>> = (props) => {
    const mergedConfig = useMergedConfig(config, props)
    const tableMinWidthRaw = mergedConfig.columns.reduce((sum, col) => {
      if (col.width !== undefined) return sum + (typeof col.width === 'number' ? col.width : 0)
      return sum + (col.minWidth ?? 0)
    }, 0)
    const tableMinWidth = (mergedConfig.selectable ? CHECKBOX_COL_WIDTH : 0) + tableMinWidthRaw

    const onSelectionChange = props.onSelectionChange

    // Use selection hook to manage checkbox state and handlers
    const selection = useListSelection(props.value, config.getterId, mergedConfig.selectable, props.selectedIds, onSelectionChange)
    const { selectedIds, isAllSelected, isSomeSelected, handleToggleAll, handleToggleRow } = selection

    const wrapRef = useRef<HTMLDivElement | null>(null)
    const headerRowRef = useRef<HTMLTableRowElement | null>(null)
    const hasHeaderMeasuredRef = useRef(false)
    // Refs for RAF-throttled scroll: scrollRafRef guards duplicate frames, latestScrollTopRef
    // always holds the most recent scrollTop so the RAF reads the freshest value even if
    // intermediate scroll events were skipped.
    const scrollRafRef = useRef<number | null>(null)
    const latestScrollTopRef = useRef(0)
    const [containerHeight, setContainerHeight] = useState(0)
    const [containerWidth, setContainerWidth] = useState(0)
    const [scrollTopState, setScrollTopState] = useState(0)
    const [headerHeight, setHeaderHeight] = useState(LIST_HEADER_HEIGHT)

    const overscan = Math.max(1, mergedConfig.virtualizedOptions.overscan)
    // Refs for window-change detection — updated on every render so the handleScroll RAF reads fresh values
    // without needing to re-create the callback. The initial value is overwritten immediately on first render.
    const rowStrideRef = useRef(0)
    const headerHeightScrollRef = useRef(LIST_HEADER_HEIGHT)
    const overscanScrollRef = useRef(overscan)
    // lastFiredScrollTopRef tracks the DOM scrollTop at the last actual setScrollTopState call.
    // The skip check compares both sides in DOM-coordinate space so non-scroll renders cannot
    // reset the reference point into a stale state-coordinate value (causing a stuck dead zone).
    const lastFiredScrollTopRef = useRef(0)

    // Reset scroll to top when token changes
    useEffect(() => {
      if (props.scrollResetToken === undefined) return
      if (wrapRef.current) wrapRef.current.scrollTop = 0
      latestScrollTopRef.current = 0
      lastFiredScrollTopRef.current = 0
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
      setScrollTopState(0)
    }, [props.scrollResetToken])

    const handleScroll: TableContainerProps['onScroll'] = (e) => {
      const t = e.currentTarget
      const newScrollTop = Math.round(t.scrollTop)
      latestScrollTopRef.current = newScrollTop

      // nearEnd check is urgent — fire immediately outside RAF
      if (t.scrollHeight - t.scrollTop - t.clientHeight <= mergedConfig.nearEndThreshold) {
        props.onNearEnd?.()
      }

      // Throttle virtualization state updates to one per animation frame.
      if (scrollRafRef.current !== null) return
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null
        // Always read the real DOM scrollTop inside the RAF — do NOT rely on latestScrollTopRef.
        // latestScrollTopRef can be stale if browser scroll-anchoring silently adjusts scrollTop
        // (e.g. when bottomSpacer grows after loadMore) without firing a new scroll event.
        const domScrollTop = Math.round(wrapRef.current?.scrollTop ?? latestScrollTopRef.current)
        latestScrollTopRef.current = domScrollTop

        // Skip scroll state update if the virtual window (start row) wouldn't change.
        // Both sides are computed from DOM-coordinate values (lastFiredScrollTopRef and domScrollTop)
        // so non-scroll renders cannot reset the dead zone into a stale state-coordinate value.
        const rs = rowStrideRef.current
        const lastFiredAdj = Math.max(0, lastFiredScrollTopRef.current - headerHeightScrollRef.current)
        const lastFiredStart = rs > 0 ? Math.max(0, Math.floor(lastFiredAdj / rs) - overscanScrollRef.current) : 0
        const adjustedScrollTop = Math.max(0, domScrollTop - headerHeightScrollRef.current)
        const newStart = rs > 0 ? Math.max(0, Math.floor(adjustedScrollTop / rs) - overscanScrollRef.current) : 0
        if (newStart === lastFiredStart) return

        lastFiredScrollTopRef.current = domScrollTop
        setScrollTopState(domScrollTop)
      })
    }

    // Cancel any pending scroll RAF on unmount to prevent setState on an unmounted component.
    useEffect(() => {
      return () => {
        if (scrollRafRef.current !== null) {
          cancelAnimationFrame(scrollRafRef.current)
          scrollRafRef.current = null
        }
      }
    }, [])

    useLayoutEffect(() => {
      if (!wrapRef.current) return

      // Sync scrollTopState from actual DOM scroll position on mount.
      // Guards against stale state when the component is reused across navigations
      // (browser may have preserved a non-zero scrollTop while React state reset to 0).
      const domScrollTop = wrapRef.current.scrollTop
      if (domScrollTop > 0) {
        const rounded = Math.round(domScrollTop)
        lastFiredScrollTopRef.current = rounded
        setScrollTopState(rounded)
      }

      const updateSize = () => {
        const el = wrapRef.current
        if (!el) return
        const nextHeight = el.clientHeight
        const nextWidth = el.clientWidth
        setContainerHeight((prev) => (prev === nextHeight ? prev : nextHeight))
        setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth))
      }

      updateSize()

      const observer = new ResizeObserver(() => updateSize())
      observer.observe(wrapRef.current)
      return () => observer.disconnect()
    }, [])

    useLayoutEffect(() => {
      hasHeaderMeasuredRef.current = false
    }, [mergedConfig.columns.length])

    useLayoutEffect(() => {
      if (hasHeaderMeasuredRef.current) return
      const measured = headerRowRef.current?.offsetHeight ?? 0
      if (measured > 0) {
        hasHeaderMeasuredRef.current = true
        setHeaderHeight(measured)
      }
    }, [mergedConfig.columns.length])

    const windowed = useMemo(() => {
      const totalItems = props.value.length
      if (totalItems === 0) {
        return { start: 0, end: -1, topSpacerHeight: 0, bottomSpacerHeight: 0 }
      }
      // Container not yet measured — render from start with no spacers to avoid
      // the bottom-clamp formula producing start > 0 with a stale visibleCount of 1.
      if (containerHeight === 0) {
        const defaultVisible = overscan * 2 + 1
        return { start: 0, end: Math.min(totalItems - 1, defaultVisible), topSpacerHeight: 0, bottomSpacerHeight: 0 }
      }
      const { rowHeight, rowSpacing } = mergedConfig
      const intRowHeight = Math.round(rowHeight)
      const intRowSpacing = Math.round(rowSpacing)
      const rowStride = intRowHeight + intRowSpacing
      const effectiveScrollTop = scrollTopState
      const safeViewportHeight = Math.max(1, containerHeight - headerHeight)
      const adjustedScrollTop = Math.max(0, effectiveScrollTop - headerHeight)
      const visibleCount = Math.ceil(safeViewportHeight / rowStride)
      let start = Math.max(0, Math.floor(adjustedScrollTop / rowStride) - overscan)
      let end = Math.min(totalItems - 1, start + visibleCount + overscan * 2)

      // NOTE: The bottom-clamp that previously pinned start when end===totalItems-1
      // has been removed. That clamp decoupled start from scrollTopState, causing a
      // visual jump when loadMore added items (clamp released → start changed even
      // though scrollTopState was unchanged).

      const topSpacerHeight = resolveListSpacerHeight(start, intRowHeight, intRowSpacing)
      const bottomSpacerHeight = resolveListSpacerHeight(totalItems - end - 1, intRowHeight, intRowSpacing)

      return { start, end, topSpacerHeight, bottomSpacerHeight }
    }, [props.value.length, headerHeight, overscan, mergedConfig, scrollTopState, containerHeight])

    // Keep refs in sync for RAF window-change detection (read in handleScroll, never during render).
    rowStrideRef.current = Math.round(mergedConfig.rowHeight) + Math.round(mergedConfig.rowSpacing)
    headerHeightScrollRef.current = headerHeight
    overscanScrollRef.current = overscan

    const resolvedWidths = useMemo(
      () => resolveFlexColumnWidths(mergedConfig.columns, mergedConfig.selectable, containerWidth),
      [mergedConfig.columns, mergedConfig.selectable, containerWidth]
    )

    const rowsToRender = useMemo(
      () => (windowed.end >= windowed.start ? props.value.slice(windowed.start, windowed.end + 1) : []),
      [props.value, windowed.start, windowed.end]
    )
    const rootClasses = [classes.root, classes.virtualized].filter(Boolean).join(' ')
    return (
      <ViewListStyled className={rootClasses} style={mergedConfig.rootStyle}>
        <TableContainer ref={wrapRef} className={classes.scrollContainer} onScroll={handleScroll}>
          <Table size='small' className={classes.table} sx={{ minWidth: tableMinWidth }}>
            {renderColGroup(resolvedWidths, mergedConfig.selectable)}
            <TableHead className={classes.tableHeader}>
              <TableRow ref={headerRowRef}>
                {mergedConfig.selectable && (
                  <TableCell padding='checkbox' className={classes.tableHeaderCell}>
                    <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleToggleAll} />
                  </TableCell>
                )}
                {mergedConfig.columns.map((col, colIndex) => (
                  <TableCell
                    key={String(col.field)}
                    className={classes.tableHeaderCell}
                    align={col.align}
                    sx={mapSxTableCell(col, resolvedWidths[colIndex])}
                  >
                    {col.label ?? String(col.field)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {windowed.topSpacerHeight > 0 ? (
                <TableRow className={classes.tableSpacerRow}>
                  <TableCell
                    colSpan={mergedConfig.columns.length + (mergedConfig.selectable ? 1 : 0)}
                    className={classes.tableSpacerCell}
                    sx={{ height: windowed.topSpacerHeight, padding: 0, border: 0 }}
                  />
                </TableRow>
              ) : null}

              {rowsToRender.map((row, offset) => {
                const index = windowed.start + offset
                const rowKey = config.getterId(row, index) ?? index
                const isSelected = selectedIds.includes(rowKey)
                const { rowHeight, cellPadding } = mergedConfig
                return (
                  <TableRow key={rowKey} hover sx={{ height: rowHeight }} selected={isSelected}>
                    {mergedConfig.selectable && (
                      <TableCell padding='checkbox' className={classes.tableBodyCell} sx={{ height: rowHeight, padding: cellPadding }}>
                        <Checkbox checked={isSelected} onChange={() => handleToggleRow(rowKey)} />
                      </TableCell>
                    )}
                    {mergedConfig.columns.map((col, colIndex) => (
                      <TableCell
                        key={`${String(rowKey)}-${String(col.field)}`}
                        className={classes.tableBodyCell}
                        align={col.align}
                        sx={mapSxTableCell(col, resolvedWidths[colIndex], { height: rowHeight, padding: cellPadding })}
                      >
                        <Box className={classes.tableCellContent}>{renderCellValue(col, row, index)}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}

              {windowed.bottomSpacerHeight > 0 ? (
                <TableRow className={classes.tableSpacerRow}>
                  <TableCell
                    colSpan={mergedConfig.columns.length + (mergedConfig.selectable ? 1 : 0)}
                    className={classes.tableSpacerCell}
                    sx={{ height: windowed.bottomSpacerHeight, padding: 0, border: 0 }}
                  />
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </ViewListStyled>
    )
  }
  return ViewListVirtualized
}
//#endregion
//#region Main
export function createViewList<T>(config: IViewListConfig<T>) {
  const ViewListNormal = createViewListNormal<T>(config)
  const ViewListVirtualized = createViewListVirtualized<T>(config)

  const ViewList: FC<IViewListProps<T>> = (props) => {
    const renderStrategy = props.renderStrategy ?? config.renderStrategy ?? 'normal'
    if (renderStrategy === 'virtualized') {
      return <ViewListVirtualized {...props} />
    }
    return <ViewListNormal {...props} />
  }
  return ViewList
}

export interface IViewListLoadingProps<T> extends IViewListProps<T> {
  loadMode: LoadingModeRule
  loading?: boolean
  pagination?: TPagination
  onPageChange?: (page: number, pageSize: number) => void | Promise<void>
}

export function createViewListLoading<T>(ListComponent: ComponentType<IViewListProps<T>>) {
  const ViewListLoading: FC<IViewListLoadingProps<T>> = (props) => {
    // Guard against duplicate calls during the window between onPageChange and loading=true propagating back
    const pendingRef = useRef(false)

    // Sync pendingRef when loading resets externally (e.g. error path where onPageChange didn't resolve)
    useEffect(() => {
      if (!props.loading) pendingRef.current = false
    }, [props.loading])

    const handleNearEnd = useCallback(async () => {
      if (props.loadMode !== 'infiniteScroll') return
      if (!props.pagination?.hasNext) return
      if (props.loading) return
      if (pendingRef.current) return

      pendingRef.current = true
      try {
        const currentPage = props.pagination?.page ?? 0
        const currentPageSize = props.pagination?.pageSize ?? 20
        const nextPage = currentPage + 1
        await props.onPageChange?.(nextPage, currentPageSize)
      } finally {
        pendingRef.current = false
      }
    }, [props.loadMode, props.pagination?.hasNext, props.pagination?.page, props.pagination?.pageSize, props.loading, props.onPageChange])

    return (
      <ListComponent
        value={props.value}
        columns={props.columns}
        density={props.density}
        spacing={props.spacing}
        renderStrategy={props.renderStrategy}
        normalOptions={props.normalOptions}
        virtualizedOptions={props.virtualizedOptions}
        onNearEnd={props.loadMode === 'infiniteScroll' ? handleNearEnd : undefined}
        selectable={props.selectable}
        selectedIds={props.selectedIds}
        onSelectionChange={props.onSelectionChange}
        scrollResetToken={props.scrollResetToken}
      />
    )
  }

  return ViewListLoading
}

export default createViewList
//#endregion
